import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, History, Link as LinkIcon, MoreHorizontal, Check, Plus } from 'lucide-react';
import {
  Avatar, Badge, Button, HealthBanner, Menu, MenuDivider, MenuItem, Popover,
  Tabs, Tab, Tooltip, useToast,
} from '@vd/ui';
import { isLiveTicketEvent, parsePlacedSize, type LiveEvent, type Ticket, type ViewMode } from '@vd/types';
import { ViewToggle } from '../board/ViewToggle';
import { ColumnDot } from '../board/ColumnDot';
import { api } from '../../lib/api';
import { TicketJiraChip, TicketJiraMenuItem, TicketJiraNotes } from '../jira/JiraExportControl';
import { TicketFixes } from './TicketFixes';
import { AnnotationCanvas } from '../annotations/AnnotationCanvas';
import { WalkthroughClipNotice, WalkthroughClipProcessing, useWalkthroughClip } from './WalkthroughClip';
import { useBillingSummary } from '../billing/useBillingSummary';
import { DescriptionField } from './DescriptionField';
import { CommentsSection } from './CommentsSection';
import { DevToolsPanel } from './DevToolsPanel';
import { DebugWindow, debugTabFrom, type DebugTab } from './DebugWindow';
import { AssigneePicker } from './AssigneePicker';
import { TicketTypeControl } from './TicketTypeControl';
import { NarrationAssignmentNotes, narrationAssignmentsKey } from './NarrationAssignmentNotes';
import { useAuth } from '../auth/AuthProvider';
import { copyToClipboard } from '../../lib/clipboard';
import { useShortcutMap, useModShortcut } from '../../lib/keyboard';
import { SourceBadge, isExternalSource } from './SourceBadge';
import { teamReporterName } from './teamReporter';
import { CapturedAt } from '../../lib/When';
import { useTicketDraft } from './useTicketDraft';
import { MicDictation } from './MicDictation';
import { MoveToProjectModal } from '../modals/MoveToProjectModal';
import { DestructiveConfirm } from '../modals/DestructiveConfirm';
import { BranchNameModal } from './BranchNameModal';
import { useBoardPrefs } from '../board/boardPrefs';
import { TICKET_SCREEN } from '../../lib/screenNames';
import styles from './TicketDetail.module.css';
import { TicketCaptureMeta } from './TicketCaptureMeta';
import { clickMarksFor } from './clickMarks';
import { tabTicketTitle, useTabProject, useTabTitle, useTabWorkspaceName, useUnseenWhileHidden } from '../../lib/tabTitle';

/**
 * Toad-themed hints that appear inside the title input while the AI
 * worker is queued to generate one. Picked deterministically per
 * ticket (see hashTicketId below) so each ticket gets a stable line
 * across re-renders, but the phrasing varies across the board.
 */
const TITLE_PENDING_HINTS = [
  "Toado's drafting a title",
  "We'll catch this one for you",
  'Letting the toad name this bug',
  "Toad's chewing on a title",
  "The toad's got this one",
  'Hopping into a title',
  "Bug spotted, toad's on it",
  'Croaking out a title',
];

/**
 * Tiny djb2-ish string hash — stable across browsers, no deps. Just
 * needs to be deterministic so the same ticket always lands on the
 * same hint, and well-distributed enough that adjacent ticket ids
 * land on different hints.
 */
function hashTicketId(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export interface TicketDetailProps {
  ticketId: string;
  /** When embedded inside Single view, omit the top navigation row. */
  embedded?: boolean;
  /**
   * Opened over the board (`TicketModal`, Jason 18 Oct): going to another
   * ticket stays in the modal, over the same board, and the head leaves room
   * for the modal's expand and close buttons.
   */
  inModal?: boolean;
}

export function TicketDetail({ ticketId, embedded, inModal }: TicketDetailProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // The debugging window (24 Oct): Session and DevTools as its two tabs, opened
  // by the Session button beside Link, by Capture details' line about the
  // visit, and by the Debugging context panel's expand. `?devtools=errors`
  // opens it on that tab, the link a sent Jira issue carries (TOADO-852), and
  // `?devtools=session` on the visit. The address is dropped once the window
  // closes, so a reload does not open it again.
  const [searchParams, setSearchParams] = useSearchParams();
  const askedFor = debugTabFrom(searchParams.get('devtools'));
  const [debugTab, setDebugTab] = useState<DebugTab | null>(askedFor);
  useEffect(() => { if (askedFor) setDebugTab(askedFor); }, [askedFor]);
  const openSession = () => setDebugTab('session');
  const closeDebug = () => {
    setDebugTab(null);
    if (!searchParams.has('devtools')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('devtools');
    setSearchParams(next, { replace: true });
  };
  const toast = useToast();
  // Another ticket, in the modal when this is one: the same entry, over the
  // same board, so closing still goes straight back to it.
  const openTicket = (id: string) => navigate(`/tickets/${id}`, inModal ? { state: location.state, replace: true } : undefined);
  const { user, companies, currentCompanyId, setCurrentCompany } = useAuth();
  const { unseen, notice: noticeUnseen } = useUnseenWhileHidden();
  const currentCompany = companies.find((c) => c.id === currentCompanyId);

  const descRef = useRef<HTMLTextAreaElement>(null);
  // Deterministic random hint while a title is generating — keyed off
  // ticketId so each ticket gets its own picked phrase but it doesn't
  // re-shuffle between renders. Toad-themed for brand consistency.
  const titleHint = useMemo(() => TITLE_PENDING_HINTS[hashTicketId(ticketId) % TITLE_PENDING_HINTS.length], [ticketId]);
  // Set when arriving from the extension-handoff page. Consumed once on first
  // render after the ticket loads, then cleared so refreshes don't re-steal focus.
  const pendingFocusRef = useRef<boolean>((location.state as { focusDescription?: boolean } | null)?.focusDescription === true);
  // Preserves the "arrived from capture" signal for the life of this view so the
  // save-and-return shortcut/button only appears in that flow, not during normal nav.
  const [cameFromCapture] = useState<boolean>(
    () => (location.state as { focusDescription?: boolean } | null)?.focusDescription === true,
  );
  const [overflowAnchor, setOverflowAnchor] = useState<DOMRect | null>(null);
  const [colAnchor, setColAnchor] = useState<DOMRect | null>(null);
  const [assignAnchor, setAssignAnchor] = useState<DOMRect | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);

  const ticketQ = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.getTicket(ticketId),
  });

  const columnsQ = useQuery({
    queryKey: ['columns', ticketQ.data?.projectId],
    queryFn: () => api.listColumns(ticketQ.data!.projectId),
    enabled: !!ticketQ.data,
  });

  const membersQ = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  // The project's ticket types, for the Type control beside the assignee
  // (TOADO-434). The board's key, so a board visit has usually cached it.
  const typesQ = useQuery({
    queryKey: ['ticketTypes', ticketQ.data?.projectId],
    queryFn: () => api.listProjectTicketTypes(ticketQ.data!.projectId).then((r) => r.items),
    enabled: !!ticketQ.data,
  });

  // For prev/next navigation, load the project's ticket list.
  const projectTicketsQ = useQuery({
    queryKey: ['tickets', ticketQ.data?.projectId],
    queryFn: () => api.listTickets(ticketQ.data!.projectId).then((r) => r.items),
    enabled: !!ticketQ.data,
  });

  const projectQ = useQuery({
    queryKey: ['project', ticketQ.data?.projectId],
    queryFn: () => api.getProject(ticketQ.data!.projectId),
    enabled: !!ticketQ.data,
  });

  /**
   * The plan of the workspace THIS TICKET belongs to, not the selected one.
   *
   * A ticket opened by link can belong to a workspace other than the one in
   * the switcher, and reading the selected workspace's plan there offered a
   * paying workspace an upgrade and sent them to the wrong billing page.
   *
   * Null is "unknown", never "free": a failed request must not tell anyone to
   * upgrade. It shares the top bar's query key whenever the two agree, so on
   * the common path it costs no extra request.
   */
  const ticketCompanyId = projectQ.data?.companyId;
  const ticketBilling = useBillingSummary(ticketCompanyId);
  const companyPlan = ticketBilling?.plan;

  /**
   * The notice for a clip that has been pruned, and where it is allowed to go.
   *
   * One slot on the image row holds either a capture-health warning or this.
   * The warning wins, because something going wrong with the capture beats
   * video ageing out on schedule - but the notice is not dropped when it
   * loses, it moves under the screenshot. A walkthrough can be `partial` for
   * a failed transcript while its video was fine, so the two states co-exist,
   * and losing the only sentence that says the video went on purpose leaves
   * somebody hunting a bug that is not there.
   */
  const slotTakenByHealth = !bannerDismissed && ticketQ.data?.health !== 'complete';
  /**
   * Only offer the upgrade when the ticket's OWN workspace is known to be
   * free. Until the project resolves there is no workspace to read a plan
   * from, and guessing offers a paying workspace an upgrade it does not need.
   */
  const knownFree = Boolean(ticketCompanyId) && companyPlan === 'free';
  const clipNotice = ticketQ.data?.capture.clipExpired ? (
    <WalkthroughClipNotice
      plan={knownFree ? 'free' : ticketCompanyId && companyPlan ? 'paid' : 'unknown'}
      retentionDays={ticketBilling?.recording?.videoRetentionDays}
      {...(knownFree && ticketCompanyId
        ? {
          onUpgrade: () => {
            // The ticket's workspace, which may not be the selected one.
            if (ticketCompanyId !== currentCompanyId) setCurrentCompany(ticketCompanyId);
            navigate('/company/settings/billing');
          },
        }
        : {})}
    />
  ) : ticketQ.data?.capture.clipProcessing && !ticketQ.data.capture.clipUrl ? (
    <WalkthroughClipProcessing />
  ) : null;

  /**
   * The walkthrough clip, as a tile in the image row that plays where the
   * image is (t-cnb0zy). Only walkthrough tickets have one, so an ordinary
   * capture ticket is visually unchanged. An expired clip has no URL (the API
   * stops publishing one that could only 404) and gets `clipNotice` instead.
   */
  const capture = ticketQ.data?.capture;
  const clipMedia = useWalkthroughClip(
    capture?.clipUrl && !capture.clipExpired
      ? {
        clipUrl: capture.clipUrl,
        ...(capture.screenshotUrl ? { posterUrl: capture.screenshotUrl } : {}),
        ...(capture.clipKind ? { kind: capture.clipKind } : {}),
      }
      : null,
  );

  const currentIndex = projectTicketsQ.data?.findIndex((t) => t.id === ticketId) ?? -1;
  const prevTicket = currentIndex > 0 ? projectTicketsQ.data?.[currentIndex - 1] : undefined;
  const nextTicket = currentIndex >= 0 && projectTicketsQ.data
    ? projectTicketsQ.data[currentIndex + 1]
    : undefined;

  // Title + description draft state (sync-from-server-when-not-dirty,
  // ticketId-reset, autosave debounce, beforeunload flush) lives in
  // useTicketDraft.ts. Here we just supply the autosave callback
  // (which knows how to invoke the mutation below) and read the
  // resulting title/description for rendering.
  const draft = useTicketDraft(ticketQ.data, {
    onAutosaveDescription: (next) => updateMutation.mutate({ description: next }),
  });
  const { title, description, setTitle, setDescription } = draft;

  // Opened, for the Dashboard's Pick up where you left off (TOADO-730): once a
  // ticket has loaded; the server records it at most once a minute.
  const ticketLoaded = !!ticketQ.data;
  useEffect(() => {
    if (ticketLoaded) void api.ticketOpened(ticketId).catch(() => undefined);
  }, [ticketId, ticketLoaded]);

  // Focus management on first load when arriving from a capture.
  // Lives separately from useTicketDraft because it touches the DOM
  // directly via descRef rather than draft state.
  useEffect(() => {
    if (ticketQ.data) {
      if (pendingFocusRef.current) {
        pendingFocusRef.current = false;
        // Let Textarea mount and paint first, then focus + caret to end.
        requestAnimationFrame(() => {
          const el = descRef.current;
          if (!el) return;
          el.focus();
          const end = el.value.length;
          el.setSelectionRange(end, end);
        });
      }
    }
  }, [ticketQ.data]);

  // Keyboard: [ and ] navigate prev/next ticket, and so do K and J, as in
  // mail and issue trackers (Jason, 18 Oct). The hook ignores key events
  // whose target is editable (input/textarea/contentEditable), which is what
  // we want — these are also characters a user types. Not the arrows: the
  // drawing canvas nudges a shape with them. Not J and K when embedded in
  // Single view, whose list moves with them.
  useShortcutMap({
    '[': () => { if (prevTicket) openTicket(prevTicket.id); },
    ']': () => { if (nextTicket) openTicket(nextTicket.id); },
    ...(embedded ? {} : {
      k: () => { if (prevTicket) openTicket(prevTicket.id); },
      j: () => { if (nextTicket) openTicket(nextTicket.id); },
    }),
  });

  // Subscribe to live events
  useEffect(() => {
    return api.subscribe((e: LiveEvent) => {
      if (isLiveTicketEvent(e, ticketId)) {
        // A comment, a move or an assignment by someone else, or by an agent,
        // marks a background tab (TOADO-905); this person's own, from another
        // tab, does not.
        const byOther = e.type === 'comment.created' ? (e.comment.authorId !== user?.id || e.comment.authorType === 'mcp_client')
          : e.type === 'ticket.moved' ? (e.by !== user?.id || !!e.agent)
            : e.type === 'ticket.assigned' ? e.by !== user?.id
              : false;
        if (byOther) noticeUnseen();
        // Skip the ['ticket'] invalidation for annotation.updated.
        // AnnotationCanvas owns that event and applies it directly to
        // shape state via the merge logic. Invalidating here would
        // refetch the ticket, hand a new initialShapes prop to
        // AnnotationCanvas, and trigger its hydration effect —
        // wiping any local pending strokes the user is mid-drawing.
        // It also re-presigns the screenshot URL on every refetch,
        // which makes the carousel image flash.
        //
        // Same skip applies to thumbnail-only ticket.updated events
        // (fired by the annotation-raster endpoint to refresh board
        // card thumbnails). The detail-page carousel renders the raw
        // screenshot, not the annotated raster, so the new field is
        // irrelevant here — but a blind invalidate would re-presign
        // the screenshot URL and flash the carousel anyway.
        const isThumbnailOnlyUpdate = e.type === 'ticket.updated'
          && e.fields.thumbnailUrl !== undefined
          && Object.keys(e.fields).length === 1;
        // Where the ticket stands with Jira (TOADO-708) changes only the Jira
        // control, which reads its own query; the ticket itself is untouched.
        const isJiraOnlyUpdate = e.type === 'ticket.updated'
          && e.fields.jira !== undefined
          && Object.keys(e.fields).length === 1;
        if (isJiraOnlyUpdate) qc.invalidateQueries({ queryKey: ['jira-export', ticketId] });
        if (e.type !== 'annotation.updated' && !isThumbnailOnlyUpdate && !isJiraOnlyUpdate) {
          qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
          qc.invalidateQueries({ queryKey: ['comments', ticketId] });
        }
        if (e.type === 'comment.created' && e.comment.authorType === 'mcp_client') {
          toast.push({
            variant: 'info',
            title: `${e.comment.authorDisplayName} added a comment`,
            description: e.comment.body.slice(0, 80),
          });
        }
      }
    });
  }, [ticketId, qc, toast]);

  const updateMutation = useMutation({
    mutationFn: (fields: Partial<Pick<Ticket, 'title' | 'description'>>) =>
      api.updateTicket(ticketId, fields),
    onSuccess: (_res, fields) => {
      // Clear dirty flags for the fields that just persisted so the next
      // server sync can refresh them — e.g. AI title arrival picks up
      // automatically once the user stops typing.
      draft.markSaved({
        title: fields.title !== undefined,
        description: fields.description !== undefined,
      });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });

  /**
   * The ticket's type, changed from the page (TOADO-434).
   *
   * Its own mutation rather than the draft's: the title and description one also
   * clears their unsaved state, and a type is not a draft - it saves as it is
   * chosen, like the assignee beside it. The board's lists are refreshed too,
   * because every card there shows the type.
   */
  const typeMutation = useMutation({
    mutationFn: (typeId: string | null) => api.updateTicket(ticketId, { typeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      if (ticketQ.data) qc.invalidateQueries({ queryKey: ['tickets', ticketQ.data.projectId] });
    },
    onError: () => {
      toast.push({ variant: 'danger', title: 'The type could not be changed' });
    },
  });

  // Save pending edits, then close this tab so focus returns to the tab the
  // user was on when they captured. window.close() is a no-op for tabs the
  // extension created, so we postMessage the extension's content-script
  // bridge and let it call chrome.tabs.remove from the service worker. The
  // capture flow opens this tab with openerTabId set, so Chrome activates
  // the source tab on close. window.close() is the fallback for when this
  // page was opened outside the extension flow (e.g., a Slack link).
  /**
   * Flip a draft → ready (is_draft = false) without closing the tab. The
   * Drafts list is where users return to abandoned captures, and the
   * detail view there isn't a capture-spawned tab the SW can close. So
   * this lives parallel to `saveAndReturn` — same persistence shape,
   * different terminal step (stay on page, no postMessage to the SW).
   */
  async function markReady() {
    const ticketLoaded = ticketQ.data;
    if (!ticketLoaded?.isDraft) return;
    const pending: Partial<Pick<Ticket, 'title' | 'description'>> & { isDraft: false } = { isDraft: false };
    if (draft.isTitleDirty() && title !== ticketLoaded.title) pending.title = title;
    if (description !== ticketLoaded.description) pending.description = description;
    try {
      await updateMutation.mutateAsync(pending);
      toast.push({ variant: 'success', title: 'Ticket ready', description: 'It will appear on the board now.' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  }

  async function saveAndReturn() {
    const ticketLoaded = ticketQ.data;
    if (!ticketLoaded) return;
    const pending: Partial<Pick<Ticket, 'title' | 'description'>> & { isDraft?: false } = {};
    // Same logic as the title input's onBlur: only save the title if
    // the user actually typed in the field. While titlePending is true,
    // the empty input is a "let AI write it" placeholder state - saving
    // '' here would clear titlePending server-side and pre-empt the
    // worker. Description has no such gate; even an empty edit is fine.
    if (draft.isTitleDirty() && title !== ticketLoaded.title) pending.title = title;
    if (description !== ticketLoaded.description) pending.description = description;
    // Flip the draft bit off so the board / MCP / skill can pick this
    // ticket up. Only attach the field when the ticket is currently a
    // draft — re-sending isDraft:false on a non-draft is harmless server-
    // side (the PATCH is idempotent) but it bloats the request body and
    // makes the audit log noisier than necessary.
    if (ticketLoaded.isDraft) pending.isDraft = false;
    try {
      if (Object.keys(pending).length > 0) {
        await updateMutation.mutateAsync(pending);
      }
      toast.push({ variant: 'success', title: 'Ticket saved', description: 'Returning to your tab…' });
      window.postMessage({ type: 'toado:closeTab' }, window.location.origin);
      window.close();
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  }

  // ⌘/Ctrl+Enter triggers Save & return from anywhere on the page after a
  // capture, not just from inside the description textarea. The button
  // label advertises this shortcut, so it has to keep working when the
  // user clicks on the screenshot, the title input, or anywhere else.
  // CommentsSection has its own ⌘+Enter (post comment) and stops
  // propagation, so typing a comment doesn't accidentally close the tab.
  const saveAndReturnRef = useRef(saveAndReturn);
  saveAndReturnRef.current = saveAndReturn;
  // Cmd/Ctrl+Enter saves and returns to the source tab when this
  // ticket was opened from a capture. The cameFromCapture check is
  // inside the handler rather than gating registration so the
  // shortcut is structurally consistent — the hook is always
  // registered, just inert when there's nowhere to return to.
  // ignoreEditable: false because the title/description are
  // textareas and that's exactly when the user wants the shortcut.
  // The Comments composer's own Cmd+Enter (post comment) calls
  // stopImmediatePropagation so it doesn't bubble to this listener —
  // see CommentsSection.tsx.
  useModShortcut('Enter', () => {
    if (!cameFromCapture) return;
    void saveAndReturnRef.current();
  }, { ignoreEditable: false });

  const moveMutation = useMutation({
    // Tagged key so external observers (e.g., SingleView's spinner
    // overlay) can react to "this ticket is currently being moved"
    // via useIsMutating without needing a prop-drilled callback. The
    // 'move-ticket' family is shared with the parent project page's
    // own moveMutation; SingleView watches both.
    mutationKey: ['move-ticket', ticketId],
    mutationFn: (args: { columnId: string; projectId?: string }) =>
      api.moveTicket(ticketId, args),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      if (ticketQ.data) {
        // Invalidate both the source project's ticket list (so the card
        // disappears from where it was) AND the destination project's list
        // (so it shows up in the new place) when it's a cross-project move.
        qc.invalidateQueries({ queryKey: ['tickets', ticketQ.data.projectId] });
        if (vars.projectId && vars.projectId !== ticketQ.data.projectId) {
          qc.invalidateQueries({ queryKey: ['tickets', vars.projectId] });
        }
      }
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ userIds, mode }: { userIds: string[]; mode: 'add' | 'replace' }) =>
      api.assignTicket(ticketId, userIds, mode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      // Taking somebody off by hand retires the walkthrough's note about them.
      qc.invalidateQueries({ queryKey: narrationAssignmentsKey(ticketId) });
      if (ticketQ.data) qc.invalidateQueries({ queryKey: ['tickets', ticketQ.data.projectId] });
    },
  });

  // This tab's title and icon: the ticket, its project's mark, and a dot when
  // someone else changed it while the tab was in the background (TOADO-905).
  const tabProject = useTabProject(ticketQ.data?.projectId);
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({
    parts: [ticketQ.data ? tabTicketTitle(ticketQ.data.title) : null, tabProject?.name, tabWorkspace],
    brand: !ticketQ.data,
    project: tabProject,
    unseen,
  });

  // The clicks to number on the screenshot (TOADO-919), worked out once per ticket read.
  const clickMarks = useMemo(() => (ticketQ.data ? clickMarksFor(ticketQ.data) : null), [ticketQ.data]);

  // Failed, it says so and offers to try again, rather than loading for ever (TOADO-988, review round 7).
  if (ticketQ.isError && !ticketQ.data) {
    return (
      <div role="alert" data-testid="ticket-load-error" style={{ padding: 40, display: 'flex', gap: 12, alignItems: 'center', color: 'var(--text-secondary)' }}>
        This ticket could not be loaded.
        <Button size="sm" variant="secondary" onClick={() => { void ticketQ.refetch(); }}>Try again</Button>
      </div>
    );
  }
  if (ticketQ.isLoading || !ticketQ.data) {
    return <div style={{ padding: 40, color: 'var(--text-tertiary)' }}>Loading ticket…</div>;
  }
  const ticket = ticketQ.data;
  const column = columnsQ.data?.find((c) => c.id === ticket.columnId);
  const creator = membersQ.data?.find((m) => m.user.id === ticket.createdBy)?.user;
  const assigneeUsers = (membersQ.data ?? [])
    .filter((m) => ticket.assignees.includes(m.user.id))
    .map((m) => m.user);
  // The size a pin was placed at (TOADO-828), for a pin that recorded one.
  const placedAt = ticket.captureMode === 'pin'
    ? parsePlacedSize(ticket.capture?.browser?.viewport
      ? { ...ticket.capture.browser.viewport, frame: ticket.capture.browser.pinFrame }
      : undefined)
    : undefined;

  const copyLink = async () => {
    await copyToClipboard(`${window.location.origin}/tickets/${ticket.id}`);
    toast.push({ variant: 'success', title: 'Link copied' });
  };

  return (
    // Named for pins (TOADO-701), so what is on it stays on every one of its
    // debugging tabs, which are screens of their own, and stays off the
    // board's other views when the board's single view shows it.
    <div className={styles.page} data-toado-view={TICKET_SCREEN}>
      {deleting && (
        <div className={styles.deletingOverlay} role="status" aria-live="polite" aria-label="Deleting ticket">
          <div className={styles.deletingCard}>
            <div className={styles.deletingSpinner} aria-hidden />
            <div>Deleting ticket…</div>
          </div>
        </div>
      )}
      {!embedded && (
        <div className={inModal ? `${styles.head} ${styles.headInModal}` : styles.head}>
          <div className={styles.breadcrumb}>
            <button className={styles.back} onClick={() => navigate(-1)} aria-label="Back">
              <ArrowLeft size={14} />
            </button>
            {currentCompany && (
              <>
                <button
                  className={styles.crumbLink}
                  onClick={() => navigate('/')}
                >
                  {currentCompany.name}
                </button>
                <span className={styles.sep}>/</span>
              </>
            )}
            <button
              className={styles.crumbLink}
              onClick={() => navigate(`/projects/${ticket.projectId}/board`)}
            >
              {projectQ.data?.name ?? 'Project'}
            </button>
            <span className={styles.sep}>/</span>
            <span className={styles.current}>{ticket.title.slice(0, 40)}{ticket.title.length > 40 ? '…' : ''}</span>
          </div>
          <div className={styles.headActions}>
            {/* Not in the modal: the board it would go to is the one under it (Jason, 18 Oct). */}
            {!inModal && <ViewToggle
              // No view is "current" on the ticket detail page — this
              // control is a navigator into the project board, not a
              // toggle of the page itself. Passing null leaves every
              // button un-highlighted.
              viewMode={null}
              onChange={(mode: ViewMode) => {
                useBoardPrefs.getState().patch(ticket.projectId, { viewMode: mode });
                if (mode === 'single') {
                  // Pre-select this ticket so SingleView lands on it
                  // (preserves the old "Queue view" button's behavior).
                  try { sessionStorage.setItem(`vd-single-selected-${ticket.projectId}`, ticket.id); } catch { /* noop */ }
                }
                navigate(`/projects/${ticket.projectId}/board`);
              }}
            />}
            <Button
              variant="secondary"
              size="sm"
              disabled={!prevTicket}
              onClick={() => prevTicket && openTicket(prevTicket.id)}
              title={prevTicket ? `${prevTicket.title} (K)` : 'No previous ticket'}
            >
              ← Prev
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!nextTicket}
              onClick={() => nextTicket && openTicket(nextTicket.id)}
              title={nextTicket ? `${nextTicket.title} (J)` : 'No next ticket'}
            >
              Next →
            </Button>
            {currentIndex >= 0 && projectTicketsQ.data && (
              <span className={styles.counter}>
                {currentIndex + 1} / {projectTicketsQ.data.length}
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.body}>
        <div className={styles.left}>
          <AnnotationCanvas
            ticketId={ticket.id}
            screenshotUrl={ticket.capture.screenshotUrl}
            // The Ticket payload returns annotations as an array (the
            // schema allows multiple targets per ticket — ticket-level,
            // attachment-level, etc.). Today we only use the ticket-
            // level row; pull its snapshot through to the canvas so
            // every team member opening this ticket sees the same
            // shapes, not a blank canvas + the original author's
            // localStorage-only drawings.
            initialShapes={ticket.annotations.find((a) => a.targetId === ticket.id)?.snapshot}
            initialAnnotationVersion={ticket.annotations.find((a) => a.targetId === ticket.id)?.version}
            // Where the person clicked on the captured page, numbered (TOADO-919).
            clickMarks={clickMarks}
            attachments={ticket.attachments}
            /**
             * One slot, so the two things that want it are ordered.
             *
             * A capture health warning says something went wrong with this
             * ticket and beats a notice about video that aged out on schedule.
             * The clip notice takes the slot only when there is no warning to
             * displace.
             */
            carouselRightSlot={
              !bannerDismissed && ticket.health !== 'complete' ? (
                <CaptureHealthNote
                  variant={ticket.health === 'failed' ? 'danger' : 'warning'}
                  title={ticket.health === 'failed' ? 'Capture failed' : 'Capture partial'}
                  description={ticket.healthDetail.message}
                  onDismiss={() => setBannerDismissed(true)}
                />
              ) : clipNotice
            }
            {...(clipMedia ? { stageMedia: clipMedia } : {})}
          />
          {/*
            * When the health warning took the row, the notice comes down
            * here instead of being dropped. A walkthrough can be `partial`
            * for a failed transcript while its video was fine, so the two
            * states co-exist - and losing the only sentence that says the
            * video was pruned on purpose leaves somebody hunting a bug that
            * is not there.
            */}
          {slotTakenByHealth && ticket.capture.clipExpired && (
            <div style={{ marginTop: 'var(--sp-3)' }}>{clipNotice}</div>
          )}
        </div>

        <div className={styles.right}>
          <div className={styles.topRow}>
            {column && (
              <button
                className={styles.colPill}
                onClick={(e) => setColAnchor(e.currentTarget.getBoundingClientRect())}
              >
                <ColumnDot tone={column.tone} size={6} />
                {column.name}
              </button>
            )}
            <div className={styles.topActions}>
              <Button variant="secondary" size="sm" leftIcon={<LinkIcon size={14} />} onClick={copyLink} aria-label="Copy link">
                Link
              </Button>
              <Button variant="secondary" size="sm" leftIcon={<History size={14} />} onClick={openSession} data-tour-anchor="ticket.session">
                Session
              </Button>
              <button
                className={styles.iconBtn}
                onClick={(e) => setOverflowAnchor(e.currentTarget.getBoundingClientRect())}
                aria-label="More"
              >
                <MoreHorizontal size={14} />
              </button>
            </div>
          </div>

          <section className={styles.titleSection}>
            <div className={styles.titleInputWrap}>
              <input
                className={styles.title}
                value={title}
                // A board's sample keeps its words (TOADO-988); the server refuses a change.
                readOnly={ticket.isSample === true}
                // Native placeholder is suppressed when the AI is queued
                // (titleOverlay below renders the styled hint with wave
                // dots instead). When AI is off / already done, fall back
                // to a normal "Untitled ticket" placeholder.
                placeholder={ticket.titlePending ? '' : 'Untitled ticket'}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => {
                  // Don't fire a save just because the user clicked away
                  // from an empty title field. While titlePending is true,
                  // the empty input is a "let AI write it" placeholder
                  // state, not a manual edit — saving '' here would clear
                  // titlePending server-side and pre-empt the worker.
                  if (!draft.isTitleDirty()) return;
                  if (title === ticket.title) return;
                  updateMutation.mutate({ title });
                }}
              />
              {ticket.titlePending && !title && (
                <div className={styles.titleOverlay} aria-hidden="true">
                  <span>{titleHint}</span>
                  <span className={styles.titleHintDots}>
                    <span /><span /><span />
                  </span>
                </div>
              )}
            </div>
            <div className={styles.meta}>
              {/* For an external report the workspace member shown as
                  creator is a stand-in (tickets need an author), so leading
                  with their avatar would be actively misleading. Show who
                  actually reported it instead. */}
              {isExternalSource(ticket.source) ? (
                <>
                  <SourceBadge source={ticket.source} />
                  <span>
                    {ticket.reporterName || ticket.reporterEmail || 'Anonymous reporter'}
                  </span>
                  {(ticket.reporterName || ticket.reporterEmail) && (
                    <Tooltip content="Self-reported and not verified. No email confirmation was sent.">
                      <span className={styles.unverifiedTag}>unverified</span>
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  {creator && <Avatar name={creator.name} size="xs" />}
                  <span>{creator?.name ?? ticket.createdBy}</span>
                  {ticket.source && ticket.source !== 'extension' && (
                    <SourceBadge source={ticket.source} showLabel={false} />
                  )}
                </>
              )}
              <span className={styles.dotSep} />
              <CapturedAt at={ticket.createdAt} reporterZone={(ticket.capture?.browser as { timezone?: string } | undefined)?.timezone} />
              <TicketCaptureMeta ticket={ticket} placedAt={placedAt} onOpenSession={openSession} />
              {/* Where it stands with Jira, as its card shows it; every Jira
                  action is in the three-dots menu (Jason, 15 Oct). */}
              <TicketJiraChip ticketId={ticket.id} projectId={ticket.projectId} separator={<span className={styles.dotSep} />} />
            </div>
            <TicketJiraNotes ticketId={ticket.id} projectId={ticket.projectId} />
            <TicketFixes ticketId={ticket.id} />
            <div className={styles.assigneeRow}>
              <span className={styles.assignLabel}>Assigned</span>
              <div className={styles.assigneeList}>
                {assigneeUsers.map((u) => (
                  <Tooltip key={u.id} content={u.name}>
                    <Avatar name={u.name} size="sm" />
                  </Tooltip>
                ))}
                <button
                  className={styles.assignBtn}
                  onClick={(e) => setAssignAnchor(e.currentTarget.getBoundingClientRect())}
                >
                  <Plus size={12} />
                  {assigneeUsers.length === 0 ? 'Assign' : null}
                </button>
              </div>
              <TicketTypeControl
                current={ticket.type}
                types={typesQ.data}
                disabled={typeMutation.isPending || !typesQ.data}
                onChange={(typeId) => typeMutation.mutate(typeId)}
              />
            </div>
            <NarrationAssignmentNotes ticketId={ticketId} projectId={ticket.projectId} />
          </section>

          <section className={styles.section} data-tour-anchor="ticket.description">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionLabel}>Description</div>
              {/* key={ticket.id} forces a fresh MicDictation instance on
                  every ticket navigation. Without it, the mount-only
                  auto-start effect would only fire on the very first
                  ticket the user opens — subsequent tickets opened via
                  in-app links would reuse the existing component
                  instance and skip auto-start. */}
              {!ticket.isSample && (
                <MicDictation
                  key={ticket.id}
                  textareaRef={descRef}
                  value={description}
                  onChange={setDescription}
                />
              )}
            </div>
            {ticket.isSample && (
              <p className={styles.sampleNote} data-testid="ticket-sample-note">
                A sample, so its words stay as they are. Delete it whenever you like.
              </p>
            )}
            <DescriptionField
              ref={descRef}
              className={styles.desc}
              value={description}
              placeholder="Describe what's wrong, how to reproduce, and what you expected…"
              readOnly={ticket.isSample === true}
              members={(membersQ.data ?? []).map((m) => m.user)}
              currentUserId={user?.id}
              onChange={setDescription}
              onBlur={() => description !== ticket.description && updateMutation.mutate({ description })}
            />
            {cameFromCapture && (
              <div className={styles.saveAndReturnRow}>
                <Button variant="primary" size="sm" onClick={() => void saveAndReturn()} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Save & return (⌘/Ctrl+Enter)'}
                </Button>
              </div>
            )}
            {!cameFromCapture && ticket.isDraft && (
              <div className={styles.saveAndReturnRow}>
                <Button variant="primary" size="sm" onClick={() => void markReady()} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Mark ready'}
                </Button>
              </div>
            )}
          </section>

          <section className={styles.section} data-tour-anchor="ticket.comments">
            <CommentsSection ticketId={ticket.id} members={membersQ.data ?? []} />
          </section>

          <section className={styles.devtools}>
            <div className={styles.devtoolsLabel}>Debugging context</div>
            <DevToolsPanel ticket={ticket} onExpand={setDebugTab} />
          </section>
          <DebugWindow ticket={ticket} open={debugTab !== null} onClose={closeDebug} tab={debugTab ?? 'session'} onTab={setDebugTab} teamReporter={teamReporterName(ticket, membersQ.data)} />
        </div>
      </div>

      {/* Column change */}
      <Popover
        open={!!colAnchor}
        onClose={() => setColAnchor(null)}
        anchor={colAnchor}
        placement="bottom-start"
      >
        <Menu>
          {columnsQ.data?.map((c) => (
            <MenuItem
              key={c.id}
              onClick={() => { moveMutation.mutate({ columnId: c.id }); setColAnchor(null); }}
              leftIcon={c.id === ticket.columnId ? <Check size={14} /> : <span style={{ width: 14 }} />}
            >
              <span className={styles.colMenuName}><ColumnDot tone={c.tone} />{c.name}</span>
            </MenuItem>
          ))}
        </Menu>
      </Popover>

      {/* Overflow */}
      <Popover
        open={!!overflowAnchor}
        onClose={() => setOverflowAnchor(null)}
        anchor={overflowAnchor}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem
            onClick={async () => {
              setOverflowAnchor(null);
              try {
                const copy = await api.duplicateTicket(ticket.id);
                await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId] });
                toast.push({
                  variant: 'success',
                  title: 'Ticket duplicated',
                  description: `Opened ${copy.title}`,
                  action: { label: 'Open', onClick: () => openTicket(copy.id) },
                });
              } catch (e) {
                toast.push({ variant: 'danger', title: 'Duplicate failed', description: (e as Error).message });
              }
            }}
          >
            Duplicate
          </MenuItem>
          <MenuItem onClick={() => { setOverflowAnchor(null); setMoveOpen(true); }}>
            Move to project…
          </MenuItem>
          {/* A board's sample ticket is never sent to Jira (TOADO-988). */}
          {!ticket.isSample && <TicketJiraMenuItem ticketId={ticket.id} projectId={ticket.projectId} onDone={() => setOverflowAnchor(null)} />}
          <MenuItem onClick={() => { setOverflowAnchor(null); setBranchOpen(true); }}>
            {ticket.branchName ? 'Edit branch name…' : 'Set branch name…'}
          </MenuItem>
          <MenuItem
            data-testid="sweep-skip"
            onClick={() => {
              setOverflowAnchor(null);
              toast.push({
                variant: 'info',
                title: 'Download not available yet',
                description: 'Capture-ZIP export ships with object-storage integration (Phase D).',
              });
            }}
          >
            Download capture as ZIP
          </MenuItem>
          <MenuDivider />
          <MenuItem
            onClick={async () => {
              setOverflowAnchor(null);
              try {
                await api.archiveTicket(ticket.id);
                // Invalidate both the active list (card disappears from board)
                // AND the archived list (card appears on the archive page).
                await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId] });
                await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId, 'archived'] });
                toast.push({
                  variant: 'success',
                  title: 'Ticket archived',
                  description: 'Restore it from the Archive view within 30 days.',
                  action: {
                    label: 'Undo',
                    onClick: async () => {
                      try {
                        await api.restoreTicket(ticket.id);
                        await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId] });
                        await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId, 'archived'] });
                        toast.push({ variant: 'info', title: 'Restored' });
                      } catch (e) {
                        toast.push({ variant: 'danger', title: 'Restore failed', description: (e as Error).message });
                      }
                    },
                  },
                });
                navigate(`/projects/${ticket.projectId}/board`);
              } catch (e) {
                toast.push({ variant: 'danger', title: 'Archive failed', description: (e as Error).message });
              }
            }}
          >
            Archive
          </MenuItem>
          <MenuItem destructive onClick={() => { setOverflowAnchor(null); setDeleteOpen(true); }}>
            Delete…
          </MenuItem>
        </Menu>
      </Popover>

      <BranchNameModal
        open={branchOpen}
        onClose={() => setBranchOpen(false)}
        ticketId={ticket.id}
        projectId={ticket.projectId}
        currentBranch={ticket.branchName}
      />

      <MoveToProjectModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        currentProjectId={ticket.projectId}
        onMove={(projectId, columnId) => {
          // projectId MUST go through to the server — without it, the ticket
          // stays in its current project and only the column changes, or the
          // move 400s because the columnId doesn't belong to the current
          // project. This used to drop projectId on the floor via `void`.
          moveMutation.mutate({ columnId, projectId });
          setMoveOpen(false);
          toast.push({
            variant: 'success',
            title: 'Ticket moved',
          });
        }}
      />

      <DestructiveConfirm
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          // Close the modal so the spinner overlay (rendered at the page
          // level) is visible without the modal sitting on top of it.
          setDeleteOpen(false);
          setDeleting(true);
          try {
            await api.deleteTicket(ticket.id);
            await qc.invalidateQueries({ queryKey: ['tickets', ticket.projectId] });
            toast.push({
              variant: 'info',
              title: 'Ticket deleted',
              description: 'Will be permanently removed in 30 days.',
            });
            // Navigate before clearing `deleting` so the overlay stays up
            // through the redirect — clearing it here would briefly show
            // the deleted ticket page in its old shape.
            navigate(`/projects/${ticket.projectId}/board`);
          } catch (e) {
            // Failure path: clear the overlay so the user can retry.
            setDeleting(false);
            toast.push({ variant: 'danger', title: 'Delete failed', description: (e as Error).message });
          }
        }}
        title="Delete this ticket?"
        description="The ticket, capture, and annotations will be permanently deleted in 30 days. You can restore it from Archive before then."
        confirmLabel="Delete"
        holdToConfirm
      />


      {/* Assign */}
      <Popover
        open={!!assignAnchor}
        onClose={() => setAssignAnchor(null)}
        anchor={assignAnchor}
        placement="bottom-end"
        width={280}
      >
        <AssigneePicker
          allMembers={(membersQ.data ?? []).map((m) => m.user)}
          selected={ticket.assignees}
          onDone={(userIds) => {
            assignMutation.mutate({ userIds, mode: 'replace' });
            setAssignAnchor(null);
          }}
          onCancel={() => setAssignAnchor(null)}
        />
      </Popover>
    </div>
  );
}

/**
 * Compact variant of HealthBanner for inline placement next to the image
 * carousel. The full banner ate the top of the drawing area; this one is
 * a pill + optional tooltip for the longer message.
 */
function CaptureHealthNote({
  variant, title, description, onDismiss,
}: {
  variant: 'warning' | 'danger';
  title: string;
  description?: string;
  onDismiss: () => void;
}) {
  const isFail = variant === 'danger';
  return (
    <Tooltip content={description ?? title}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 28,
          padding: '0 10px',
          borderRadius: 'var(--r-full)',
          fontSize: 12,
          fontWeight: 500,
          background: isFail ? 'var(--danger-bg)' : 'var(--warning-bg)',
          color: isFail ? 'var(--danger)' : 'var(--warning)',
          border: `1px solid ${isFail ? 'var(--danger-border)' : 'var(--warning-border)'}`,
          whiteSpace: 'nowrap',
        }}
        data-testid="capture-health-note"
      >
        <span style={{
          width: 6, height: 6, borderRadius: 50,
          background: 'currentColor', display: 'inline-block',
        }} />
        {title}
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent', border: 0, color: 'currentColor',
            cursor: 'pointer', opacity: 0.6, padding: 0, marginLeft: 2,
            lineHeight: 1, fontSize: 14,
          }}
        >
          ×
        </button>
      </span>
    </Tooltip>
  );
}
